import type { AccessLevel, Role } from '@famora/core'

/**
 * Darf diese Person das sehen?
 *
 * ⚠️ Beim Laden (`undefined`) ist die Antwort NEIN. Ein optimistisches Ja lässt
 * Bedienung aufblitzen, die die Person nicht hat, und der erste Klick läuft in
 * ein 403 — schlimmer als ein Moment ohne den Knopf.
 */
export function darfSehen(caps: Set<string> | null | undefined, capability: string): boolean {
  return caps?.has(capability) ?? false
}

/**
 * Die Farben der Familie.
 *
 * Die App hat keine eigene Akzentfarbe für Personen — sie trägt die Farben
 * der Menschen darin. Acht Töne, die sich auch bei den häufigen
 * Farbfehlsichtigkeiten unterscheiden.
 *
 * ⚠️ Spiegel von `--person-1…8` in tokens.css. Ein Test hält beide Listen
 * deckungsgleich; die Werte hier braucht JavaScript (mitgliedsFarbe), die
 * dort braucht CSS.
 */
export const MITGLIED_PALETTE = [
  '#c52d5d',
  '#b34a1b',
  '#995d00',
  '#796b00',
  '#527500',
  '#007b38',
  '#00786a',
  '#0071a3',
] as const

const HEX = /^#[0-9a-fA-F]{6}$/

/** Stabile Ersatzfarbe aus der Id, damit sie über Reloads gleich bleibt. */
export function mitgliedsFarbe(gespeichert: string | null | undefined, memberId: string): string {
  if (gespeichert && HEX.test(gespeichert)) return gespeichert.toLowerCase()
  let summe = 0
  for (const z of memberId) summe = (summe * 31 + z.charCodeAt(0)) % 100_000
  return MITGLIED_PALETTE[summe % MITGLIED_PALETTE.length] ?? MITGLIED_PALETTE[0]
}

/** Familiensprache, nicht Systemsprache. */
export function rollenName(role: Role): string {
  const namen: Record<Role, string> = {
    owner: 'Eigentümer',
    adult: 'Erwachsen',
    teen: 'Jugendlich',
    child: 'Kind',
    guest: 'Gast',
  }
  return namen[role]
}

/** Stufen als Tätigkeit benennen, nicht als Fachwort („read“ sagt niemandem etwas). */
export function stufenName(level: AccessLevel): string {
  const namen: Record<AccessLevel, string> = {
    none: 'Nichts',
    read: 'Mitlesen',
    write: 'Mitarbeiten',
    manage: 'Verwalten',
  }
  return namen[level]
}

/**
 * Die Vorschauzahl als Satz — die Zahl allein sagt nicht, was sie bedeutet.
 * Bei unzählbaren Modulen wird das gesagt, nicht eine Null erfunden.
 */
export function zahlAlsSatz(name: string, anzahl: number | null): string {
  if (anzahl === null) return `Wie viel ${name} sieht, lässt sich hier nicht zählen.`
  if (anzahl === 0) return `${name} sieht damit noch nichts.`
  if (anzahl === 1) return `${name} sieht damit einen Eintrag.`
  return `${name} sieht damit ${anzahl} Einträge.`
}

/**
 * Die Schriftfarbe, die auf einer beliebigen Farbe lesbar ist.
 *
 * ⚠️ Gerechnet, nicht nachgeschlagen: eine Person darf eine eigene Farbe
 * speichern, die in keiner Palette steht. Entschieden wird über die relative
 * LUMINANZ (WCAG), nicht über das Mittel der drei Kanäle — reines Blau hat
 * ein Mittel von 85 und wirkt damit „hell", ist aber sehr dunkel.
 */
export function lesbareFarbeAuf(farbe: string): string {
  const roh = farbe.replace('#', '')
  const voll =
    roh.length === 3
      ? roh
          .split('')
          .map((z) => z + z)
          .join('')
      : roh
  const kanal = (von: number): number => {
    const c = parseInt(voll.slice(von, von + 2), 16) / 255
    return c <= 0.03928 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4)
  }
  const luminanz = 0.2126 * kanal(0) + 0.7152 * kanal(2) + 0.0722 * kanal(4)
  // Der Umschlagpunkt liegt dort, wo beide Kontraste gleich sind (~0.179).
  return luminanz > 0.179 ? '#1e1d23' : '#ffffff'
}
